import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Link,
  Tooltip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SignalCellularConnectedNoInternet0BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet0Bar';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DraftsIcon from '@mui/icons-material/Drafts';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RestoreIcon from '@mui/icons-material/Restore';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { EdgeAccountDigest, DigestAttention } from '../../api/config';

interface AttentionZonesProps {
  data?: DigestAttention;
  onPreview: (digest: EdgeAccountDigest) => void;
  onApprove: (digest: EdgeAccountDigest) => void;
  onArchive: (digest: EdgeAccountDigest) => void;
  onSendNow: (digest: EdgeAccountDigest) => void;
  onCancel: (digest: EdgeAccountDigest) => void;
  onSend: (digest: EdgeAccountDigest) => void;
  onRestore: (digest: EdgeAccountDigest) => void;
}

// digest_date is a bare YYYY-MM-DD string — parse as local to avoid a UTC
// off-by-one, matching the main table's date column.
const formatDigestDate = (date?: string): string => {
  if (!date) return '';
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

type ZoneTone = 'error' | 'warning' | 'info';

const ZoneSection: React.FC<{
  title: string;
  subtitle: string;
  tone: ZoneTone;
  icon: React.ReactNode;
  rows: EdgeAccountDigest[];
  truncated?: boolean;
  cap?: number;
  defaultExpanded?: boolean;
  renderReason: (digest: EdgeAccountDigest) => React.ReactNode;
  renderActions: (digest: EdgeAccountDigest) => React.ReactNode;
}> = ({ title, subtitle, tone, icon, rows, truncated, cap, defaultExpanded = true, renderReason, renderActions }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // Empty buckets render nothing, so a clean morning shows no alarming banners.
  if (rows.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        borderColor: `${tone}.main`,
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        borderLeftColor: `${tone}.main`,
      }}
    >
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box sx={{ color: `${tone}.main`, display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Chip label={rows.length} size="small" color={tone} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1, flexGrow: 1 }}>
          {subtitle}
        </Typography>
        <IconButton size="small" aria-label={expanded ? 'collapse' : 'expand'}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Table size="small">
          <TableBody>
            {rows.map((digest) => (
              <TableRow key={digest.id} hover>
                <TableCell sx={{ width: 280 }}>
                  {digest.location?.id ? (
                    <Link
                      component={RouterLink}
                      to={`/locations/${digest.location.id}/dashboard`}
                      sx={{ textDecoration: 'none' }}
                    >
                      {digest.location.name || `Location #${digest.location.id}`}
                    </Link>
                  ) : (
                    digest.location?.name || 'Unknown'
                  )}
                </TableCell>
                <TableCell sx={{ width: 110 }}>{formatDigestDate(digest.digest_date)}</TableCell>
                <TableCell sx={{ width: 200, color: 'text.secondary', fontSize: '0.8rem' }}>
                  {digest.sections_included?.join(', ') || 'None'}
                </TableCell>
                <TableCell sx={{ width: 220 }}>{renderReason(digest)}</TableCell>
                <TableCell align="right">{renderActions(digest)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {truncated && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
            Showing the first {cap}. More exist — use the filters below to find the rest.
          </Typography>
        )}
      </Collapse>
    </Paper>
  );
};

const PreviewButton: React.FC<{ digest: EdgeAccountDigest; onPreview: (d: EdgeAccountDigest) => void }> = ({
  digest,
  onPreview,
}) => (
  <Button size="small" startIcon={<PreviewIcon />} onClick={() => onPreview(digest)}>
    Preview
  </Button>
);

/**
 * Pinned morning-triage zones for the digest reports page (sc-5827). Renders
 * three sections above the main table, each hidden when empty:
 *   - Action Required: auto-send failed, location healthy → approve/send now
 *   - Blocked: outage or delivery bounce → can't act yet (no fruitless button)
 *   - Drafts — Manual Review: unscheduled drafts awaiting a CSR's approval
 * The page owns the data fetch (with polling), mutations, and dialogs; this
 * component just renders and delegates actions back via the handler props.
 */
export const AttentionZones: React.FC<AttentionZonesProps> = ({ data, onPreview, onApprove, onArchive, onSendNow, onCancel, onSend, onRestore }) => {
  if (!data) return null;

  // A clean morning renders nothing at all — no empty wrapper / borders.
  const total =
    data.action_required.length + data.blocked.length + data.manual_drafts.length + data.scheduled.length;
  if (total === 0) return null;

  const cap = data.meta?.cap;

  return (
    <Box sx={{ mb: 1 }}>
      <ZoneSection
        title="Action Required"
        subtitle="Auto-send failed and the location is healthy — approve to send now."
        tone="error"
        icon={<ErrorOutlineIcon />}
        rows={data.action_required}
        truncated={data.meta?.truncated?.action_required}
        cap={cap}
        renderReason={(d) => (
          <Tooltip
            title={[
              d.auto_send_attempted_at ? `Auto-send attempted ${new Date(d.auto_send_attempted_at).toLocaleString()}` : 'Auto-send attempted',
              d.auto_send_failure_reason ? `Reason: ${d.auto_send_failure_reason}` : null,
              d.auto_send_failure_message || null,
            ]
              .filter(Boolean)
              .join(' — ')}
            arrow
          >
            <Chip label={`auto-send failed${d.auto_send_failure_reason ? `: ${d.auto_send_failure_reason}` : ''}`} color="error" size="small" />
          </Tooltip>
        )}
        renderActions={(d) => (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <PreviewButton digest={d} onPreview={onPreview} />
            <Button size="small" startIcon={<CheckCircleIcon />} variant="outlined" onClick={() => onApprove(d)}>
              Approve
            </Button>
            <Button size="small" startIcon={<DeleteIcon />} variant="outlined" color="error" onClick={() => onArchive(d)}>
              Archive
            </Button>
          </Box>
        )}
      />

      <ZoneSection
        title="Drafts — Manual Review"
        subtitle="Hand-send queue: drafts to approve, and approved digests ready to send."
        tone="info"
        icon={<DraftsIcon />}
        rows={data.manual_drafts}
        truncated={data.meta?.truncated?.manual_drafts}
        cap={cap}
        renderReason={(d) =>
          d.status === 'approved'
            ? <Chip icon={<CheckCircleIcon />} label="approved — ready to send" color="primary" size="small" variant="outlined" />
            : <Chip label="awaiting review" size="small" variant="outlined" />
        }
        renderActions={(d) => (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <PreviewButton digest={d} onPreview={onPreview} />
            {d.status === 'approved' ? (
              <Button size="small" startIcon={<SendIcon />} variant="contained" onClick={() => onSend(d)}>
                Send
              </Button>
            ) : (
              <Button size="small" startIcon={<CheckCircleIcon />} variant="outlined" onClick={() => onApprove(d)}>
                Approve
              </Button>
            )}
            <Button size="small" startIcon={<DeleteIcon />} variant="outlined" color="error" onClick={() => onArchive(d)}>
              Archive
            </Button>
          </Box>
        )}
      />

      <ZoneSection
        title="Scheduled — Auto-Send Pending"
        subtitle="Queued to send themselves. Nothing to do unless you want to send early or cancel."
        tone="info"
        icon={<ScheduleSendIcon />}
        rows={data.scheduled}
        truncated={data.meta?.truncated?.scheduled}
        cap={cap}
        defaultExpanded={false}
        renderReason={(d) => {
          const at = d.scheduled_send_at ? new Date(d.scheduled_send_at) : null;
          const overdue = !!at && at.getTime() < Date.now();
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {at ? `Sends ${at.toLocaleString()}` : 'Scheduled'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {d.has_active_outage && (
                  <Tooltip
                    title="Location is in an active outage. This won't send at its scheduled time unless the outage clears; if it doesn't, the auto-send will fail and it'll move to Action Required / Blocked."
                    arrow
                  >
                    <Chip icon={<WarningAmberIcon />} label="at risk — outage" color="warning" size="small" variant="outlined" />
                  </Tooltip>
                )}
                {overdue && (
                  <Tooltip title="Scheduled time has passed but the auto-send job hasn't run yet — the queue may be delayed." arrow>
                    <Chip label="overdue" color="error" size="small" variant="outlined" />
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        }}
        renderActions={(d) => (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <PreviewButton digest={d} onPreview={onPreview} />
            <Button size="small" startIcon={<SendIcon />} variant="contained" onClick={() => onSendNow(d)}>
              Send Now
            </Button>
            <Button size="small" startIcon={<CancelScheduleSendIcon />} variant="outlined" color="warning" onClick={() => onCancel(d)}>
              Cancel Send
            </Button>
          </Box>
        )}
      />

      {/* Blocked lives at the bottom, collapsed by default: nothing here is
          actionable right now (outage clears itself; bounce needs HQ follow-up),
          so it shouldn't compete for attention with the act-now zones above. */}
      <ZoneSection
        title="Blocked — No Action Yet"
        subtitle="Nothing to do right now: in an outage, bounced, or cancelled. These clear on their own, need investigation, or just want archiving."
        tone="warning"
        icon={<SignalCellularConnectedNoInternet0BarIcon />}
        rows={data.blocked}
        truncated={data.meta?.truncated?.blocked}
        cap={cap}
        defaultExpanded={false}
        renderReason={(d) => {
          if (d.blocked_reason === 'bounced') {
            return (
              <Tooltip
                title={`Delivery failed${d.email_delivered_at ? ` (last attempt ${new Date(d.email_delivered_at).toLocaleString()})` : ''}. Verify the recipient address in HQ before re-sending.`}
                arrow
              >
                <Chip icon={<MarkEmailReadIcon />} label="delivery bounced" color="error" size="small" variant="outlined" />
              </Tooltip>
            );
          }
          if (d.blocked_reason === 'cancelled') {
            return (
              <Tooltip
                title={`Auto-send was cancelled${d.cancelled_by ? ` by ${d.cancelled_by}` : ''}${d.cancelled_at ? ` on ${new Date(d.cancelled_at).toLocaleString()}` : ''}. Nothing left to do — archive it (or restore if it's still within its send window).`}
                arrow
              >
                <Chip icon={<CancelIcon />} label="cancelled" color="default" size="small" variant="outlined" />
              </Tooltip>
            );
          }
          return (
            <Tooltip
              title="Covering device is in an active outage. Sending now would ship stale data; this clears automatically and moves back to Action Required once the outage ends."
              arrow
            >
              <Chip icon={<SignalCellularConnectedNoInternet0BarIcon />} label="device outage" color="warning" size="small" variant="outlined" />
            </Tooltip>
          );
        }}
        renderActions={(d) => {
          if (d.blocked_reason === 'cancelled') {
            // Restore is only possible while the original auto-send time is still
            // in the future (mirrors the model's restorable? / the main table).
            const restorable = !!d.scheduled_send_at && new Date(d.scheduled_send_at).getTime() > Date.now();
            return (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <PreviewButton digest={d} onPreview={onPreview} />
                {restorable && (
                  <Button size="small" startIcon={<RestoreIcon />} variant="outlined" onClick={() => onRestore(d)}>
                    Restore Auto-Send
                  </Button>
                )}
                <Button size="small" startIcon={<DeleteIcon />} variant="contained" color="error" onClick={() => onArchive(d)}>
                  Archive
                </Button>
              </Box>
            );
          }
          return (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <PreviewButton digest={d} onPreview={onPreview} />
            </Box>
          );
        }}
      />
    </Box>
  );
};
